import { Edit } from '@mui/icons-material';
import {
  numberToDisplayString,
  numberToCurrencyString,
  dateToLabel,
} from 'further-ui/utils';
import {
  createLpCompanyEditRoute,
  LP_COMPANY_ADD,
} from 'adminConstants/routes';
import Page from 'components/new/Page';
import SearchField from 'further-ui/components/SearchField';
import Table, { type Column } from 'further-ui/components/Table';
import ErrorBoundary from 'components/ErrorBoundary';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { useLayoutContext } from 'contexts/LayoutContext';

import useListCompanies from './useListCompanies';

const pageId = 'lp-companies';

const ListCompanies: React.FC = () => {
  const { vehicle } = useLayoutContext();

  const {
    companies,
    totalCount,
    isFetching,
    filters,
    handleChangeFilters,
    pagination,
  } = useListCompanies(pageId, vehicle?._id ?? '');

  const columns: Array<Column<(typeof companies)[number]>> = [
    {
      label: 'Company name',
      key: 'name',
    },
    {
      label: 'Total units',
      key: 'totalNumberOfUnits',
      render: (company) => numberToDisplayString(company.totalNumberOfUnits),
      disableSorting: true,
    },
    {
      label: 'Initial investment',
      key: 'totalInvestmentAmountLcy',
      disableSorting: true,
      render: (company) =>
        numberToCurrencyString(company.totalInvestmentAmountLcy, {
          currency: vehicle?.baseCurrency,
        }),
    },
    {
      label: 'Current number of units',
      key: 'currentNumberOfUnits',
      disableSorting: true,
      render: (company) => numberToDisplayString(company.currentNumberOfUnits),
    },
    {
      label: 'Hold value',
      key: 'currentHoldValueLcy',
      disableSorting: true,
      render: (company) =>
        numberToCurrencyString(company.currentHoldValueLcy, {
          currency: vehicle?.baseCurrency,
        }),
    },
    {
      label: 'Latest revaluation',
      key: 'latestRevaluationDate',
      disableSorting: true,
      render: (company) => dateToLabel(company.latestRevaluationDate),
    },
  ];

  return (
    <Page
      heading="Companies"
      spacing="lg"
      actions={[
        {
          label: 'Add company',
          link: LP_COMPANY_ADD,
        },
      ]}
    >
      <PageFilters>
        <Filter size="md">
          <SearchField
            placeholder="Search by company name"
            value={filters.searchQuery}
            onSearch={(query) => {
              handleChangeFilters({ searchQuery: query });
            }}
            autoSearchOnDebounce
            allowEmptyQuery
            fullWidth
          />
        </Filter>
      </PageFilters>
      <ErrorBoundary>
        <Table
          columns={columns}
          rows={companies}
          pagination={pagination}
          rowsTotalCount={totalCount}
          isLoading={isFetching}
          emptyMessage="No companies found"
          actions={[
            {
              label: 'Edit company',
              icon: Edit,
              link: (company) => createLpCompanyEditRoute(company.id),
            },
          ]}
        />
      </ErrorBoundary>
    </Page>
  );
};

export default ListCompanies;
